import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { useCallback } from 'react';

export type UseBrowserShareAPIProps = {
    title?: string;
    url?: string;
    text?: string;
    notSupportedFeatureMessage?: string;
    onError?: () => void;
    onAbortError?: () => void;
    onSuccess?: () => void;
}

export const useBrowserShareAPI = ({
    title,
    url,
    text,
    notSupportedFeatureMessage = 'Browser Share API is not supported',
    onAbortError,
    onError,
    onSuccess
}: UseBrowserShareAPIProps) => {
    const showNotification = useShowNotification();
    const isShareSupported = Boolean(window.navigator.share);

    const shareCallback = useCallback(() => {
        (async () => {
            if (window.navigator.share) {
                try {
                    await window.navigator.share({
                        title,
                        url,
                        text
                    });

                    if (onSuccess) {
                        onSuccess();
                    }
                } catch (error) {
                    if ((error as Error).toString().includes('AbortError')) {
                        if (onAbortError) {
                            onAbortError();
                        }
                        return;
                    }
                    if (onError) {
                        onError();
                    }
                }
            } else {
                showNotification(notSupportedFeatureMessage, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
            }
        })();
    }, [
        title,
        url,
        text,
        showNotification,
        onAbortError,
        notSupportedFeatureMessage,
        onError,
        onSuccess
    ]);

    return {
        shareCallback,
        isShareSupported
    };
};
